import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useRouter } from "next/navigation";
import { IPaymentsPeriodConfigResponse } from "@finbackoffice/clientbff-client";
import { UserStatus } from "@finbackoffice/enums";
import { ClientBFFContext } from "@finbackoffice/site-core";

export interface IUpdateSelfExclusionV2 {
    period: string;
    password: string;
}
export interface IUpdateDepositLimitsV2 {
    period: string;
    password: string;
    amount: number;
}

export const useResponsibleGamingV2 = () => {
    const client = useContext(ClientBFFContext);
    const router = useRouter();
    const [paymentsPeriodConfig, setPaymentsPeriodConfig] = useState<
        IPaymentsPeriodConfigResponse | undefined
    >();
    const [loadingPeriodConfig, setLoadingPeriodConfig] = useState<boolean>(true);

    const loadPaymentsPeriodConfig = useCallback(async () => {
        try {
            const res = await client.getPaymentsPeriodConfig();
            setPaymentsPeriodConfig(res);
        } finally {
            setLoadingPeriodConfig(false);
        }
    }, [client]);

    const updateSelfExclusion = useCallback(
        async (data: IUpdateSelfExclusionV2) => {
            const response = await client.updateProfile({
                blocked_till: data.period,
                verify_password: data.password,
            });
            if (response.status === UserStatus.Blocked) {
                router.replace("/");
            }
            return response;
        },
        [client, router],
    );

    const updateDepositLimits = useCallback(
        async (data: IUpdateDepositLimitsV2) => {
            const res = await client.updatePaymentsPeriodConfig({
                verify_password: data.password,
                max_period_amounts: [
                    {
                        amount: Number(data.amount),
                        period: Number(data.period),
                        start_from: new Date().toISOString(),
                    },
                ],
            });
            setPaymentsPeriodConfig(res);
        },
        [client],
    );

    useEffect(() => {
        void loadPaymentsPeriodConfig();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return useMemo(
        () => ({
            paymentsPeriodConfig,
            updateSelfExclusion,
            updateDepositLimits,
            loadingPeriodConfig,
        }),
        [paymentsPeriodConfig, updateSelfExclusion, updateDepositLimits, loadingPeriodConfig],
    );
};
